import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Snackbar,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  useTheme,
  useMediaQuery
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { supabase } from '../lib/supabase';

function Categories() {
  const [categories, setCategories] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });
  const [categoriesInUse, setCategoriesInUse] = useState(new Set());
  const [formData, setFormData] = useState({
    name: '',
    expense_type: '' // Valor vazio como padrão
  });

  const fetchCategories = useCallback(async () => {
    try {
      // Buscar categorias
      const { data: categoriesData, error: categoriesError } = await supabase
        .from('categories')
        .select('*')
        .eq('type', 'saida')
        .order('name');

      if (categoriesError) throw categoriesError;

      // Buscar transações ativas (não arquivadas) para cada categoria
      const { data: transactionsData, error: transactionsError } = await supabase
        .from('transactions')
        .select('category_id')
        .is('deleted_at', null)
        .not('category_id', 'is', null);

      if (transactionsError) throw transactionsError;

      // Criar um Set com os IDs das categorias em uso por transações ativas
      const inUse = new Set(transactionsData.map(t => t.category_id));
      setCategoriesInUse(inUse);
      
      setCategories(categoriesData || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
      showSnackbar('Erro ao carregar categorias');
    }
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const handleOpenDialog = (category = null) => {
    if (category) {
      setEditingCategory(category);
      setFormData({
        name: category.name,
        expense_type: category.expense_type
      });
    } else {
      setEditingCategory(null);
      setFormData({
        name: '',
        expense_type: ''
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingCategory(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const categoryData = {
        ...formData,
        type: 'saida'
      };

      if (editingCategory) {
        const { error } = await supabase
          .from('categories')
          .update(categoryData)
          .eq('id', editingCategory.id);

        if (error) throw error;
        showSnackbar('Categoria atualizada com sucesso');
      } else {
        const { error } = await supabase
          .from('categories')
          .insert([categoryData]);

        if (error) throw error;
        showSnackbar('Categoria criada com sucesso');
      }

      handleCloseDialog();
      fetchCategories();
    } catch (error) {
      console.error('Error saving category:', error);
      showSnackbar('Erro ao salvar categoria');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Tem certeza que deseja excluir esta categoria?')) {
      try {
        // Verificar se existem transações ativas usando esta categoria
        const { data: transactions, error: checkError } = await supabase
          .from('transactions')
          .select('id')
          .eq('category_id', id)
          .is('deleted_at', null)
          .limit(1);

        if (checkError) throw checkError;

        if (transactions && transactions.length > 0) {
          showSnackbar('Não é possível excluir esta categoria pois existem transações ativas associadas a ela');
          return;
        }

        const { error } = await supabase
          .from('categories')
          .delete()
          .eq('id', id);

        if (error) {
          if (error.code === '23503') { // Código de violação de chave estrangeira
            showSnackbar('Não é possível excluir esta categoria pois existem transações ativas associadas a ela');
          } else {
            throw error;
          }
          return;
        }

        showSnackbar('Categoria excluída com sucesso');
        fetchCategories();
      } catch (error) {
        console.error('Error deleting category:', error);
        showSnackbar('Erro ao excluir categoria: ' + error.message);
      }
    }
  };

  const showSnackbar = (message) => {
    setSnackbar({ open: true, message });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Box>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: isMobile ? 'column' : 'row',
        justifyContent: isMobile ? 'flex-start' : 'space-between', 
        alignItems: isMobile ? 'stretch' : 'center', 
        gap: isMobile ? 2 : 0,
        mb: 3 
      }}>
        <Typography variant={isMobile ? "h5" : "h4"} sx={{ textAlign: isMobile ? 'center' : 'left' }}>
          Categorias de Saída
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{ minWidth: isMobile ? '100%' : 'auto' }}
        >
          Nova Categoria
        </Button>
      </Box>

      {isMobile ? (
        <Box>
          {categories.map((category) => (
            <Paper key={category.id} sx={{ p: 2, mb: 2, borderRadius: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {category.name}
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <IconButton 
                    size="small" 
                    onClick={() => handleOpenDialog(category)}
                    sx={{ 
                      minWidth: '44px',
                      minHeight: '44px'
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <Tooltip title={categoriesInUse.has(category.id) ? "Não é possível excluir uma categoria que está sendo utilizada" : ""}>
                    <span>
                      <IconButton 
                        size="small" 
                        onClick={() => handleDelete(category.id)}
                        disabled={categoriesInUse.has(category.id)}
                        sx={{ 
                          minWidth: '44px',
                          minHeight: '44px'
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              </Box>
              {category.expense_type && (
                <Chip 
                  label={category.expense_type === 'fixed' ? 'Fixa' : 'Variável'}
                  size="small"
                  color={category.expense_type === 'fixed' ? 'primary' : 'info'}
                  variant="outlined"
                />
              )}
            </Paper>
          ))}
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Nome</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell align="right">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {categories.map((category) => (
                <TableRow key={category.id}>
                  <TableCell>{category.name}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {category.expense_type && (
                        <Chip 
                          label={category.expense_type === 'fixed' ? 'Fixa' : 'Variável'}
                          size="small"
                          color={category.expense_type === 'fixed' ? 'primary' : 'info'}
                          variant="outlined"
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                      <IconButton 
                        size="small" 
                        onClick={() => handleOpenDialog(category)}
                        sx={{ 
                          minWidth: '44px',
                          minHeight: '44px'
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                      <Tooltip title={categoriesInUse.has(category.id) ? "Não é possível excluir uma categoria que está sendo utilizada" : ""}>
                        <span>
                          <IconButton 
                            size="small" 
                            onClick={() => handleDelete(category.id)}
                            disabled={categoriesInUse.has(category.id)}
                            sx={{ 
                              minWidth: '44px',
                              minHeight: '44px'
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <form onSubmit={handleSubmit}>
          <DialogTitle>
            {editingCategory ? 'Editar Categoria' : 'Nova Categoria'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                name="name"
                label="Nome"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                fullWidth
                required
              />
              <FormControl fullWidth>
                <InputLabel>Tipo de Despesa</InputLabel>
                <Select
                  name="expense_type"
                  value={formData.expense_type}
                  onChange={(e) => setFormData({ ...formData, expense_type: e.target.value })}
                  label="Tipo de Despesa"
                >
                  <MenuItem value="">
                    <em>Nenhum</em>
                  </MenuItem>
                  <MenuItem value="fixed">Fixa</MenuItem>
                  <MenuItem value="variable">Variável</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancelar</Button>
            <Button type="submit" variant="contained">
              Salvar
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        message={snackbar.message}
      />
    </Box>
  );
}

export default Categories;
